import { z } from 'zod';

export const Uuid = z.string().uuid();
export const Email = z.string().email();
export const Iso = z.string().datetime();
export const Cents = z.number().int().nonnegative();

/**
 * Adapter providers supported by the BYOK credential vault.
 *
 * **ToS-blocked providers (do NOT instantiate adapters for these without
 * written provider approval — see `docs/legal/adapter-tos.md`):**
 *  - `apollo`  — Section 3 forbids BYOK third-party access without approval
 *  - `postmark` — Section 8 requires prior written consent for third-party apps
 *
 * They remain in the enum for forward-compatibility (e.g., if explicit
 * approval is later granted) but the API must reject credential creation
 * for these until the legal status changes.
 */
export const Provider = z.enum([
  'keepa',
  'gemini',
  'openrouter',
  'tavily',
  'brave',
  'hunter',
  'apollo', // ToS-blocked — see docs/legal/adapter-tos.md
  'smtp',
  'postmark', // ToS-blocked — see docs/legal/adapter-tos.md
  'sendgrid',
  'sp_api',
]);
export type Provider = z.infer<typeof Provider>;

/** Providers whose ToS prohibits Sourcito's BYOK pattern as of 2026-04-28. */
export const TOS_BLOCKED_PROVIDERS = ['apollo', 'postmark'] as const satisfies readonly Provider[];

export const RoiGrade = z.enum(['A', 'B', 'C', 'D', 'F']);
export type RoiGrade = z.infer<typeof RoiGrade>;

/**
 * R2 item 3 (2026-09-03, docs/plans/2026-09-03-graded-kernel.md D1) — the
 * ONE grade vocabulary for every cost/margin figure the finance kernel (or a
 * consumer's fallback) emits. `settlement` = derived by the kernel from
 * settlement lines and lot lineage (never estimated). `estimate` = derived
 * from a fallback (landed cost x units, supplier x pack, or a kernel figure
 * with partial-month coverage). Shared by Finance, Fee Center, the dashboard
 * margin chart, and price calibration so "settlement-grade" means the same
 * thing everywhere it appears.
 */
export const FinanceGrade = z.enum(['settlement', 'estimate']);
export type FinanceGrade = z.infer<typeof FinanceGrade>;

export const ProductStatus = z.enum(['pending', 'interested', 'pursuing', 'passed']);
export type ProductStatus = z.infer<typeof ProductStatus>;

/**
 * Supplier pipeline status — Phase 1.5 5-stage workflow + terminal `passed`.
 * The original 9-stage proposal was compressed per design doc
 * `docs/plans/2026-05-07-phase-1.5-suppliers-discovery-design.md` (Section 5):
 * "negotiating" subsumes pricing_received/approved/ordered, "active" replaces
 * "live", and `passed` is the not-pursuing terminal state.
 */
export const SupplierPipelineStatus = z.enum([
  'lead',
  'contacted',
  'replied',
  'negotiating',
  'active',
  'passed',
]);
export type SupplierPipelineStatus = z.infer<typeof SupplierPipelineStatus>;

export const DiscoveryRunStatus = z.enum(['queued', 'running', 'succeeded', 'failed']);
export type DiscoveryRunStatus = z.infer<typeof DiscoveryRunStatus>;

export const DiscoveryTrigger = z.enum(['manual', 'auto_status']);
export type DiscoveryTrigger = z.infer<typeof DiscoveryTrigger>;

/**
 * Keepa search-run state machine. Mirrors `discovery_runs` but adds
 * `cancelled` for user-aborted queued rows. `schedule` (Pass 3) inserts
 * runs in the same `queued` state.
 */
export const KeepaSearchRunStatus = z.enum([
  'queued',
  'running',
  'succeeded',
  'failed',
  'cancelled',
]);
export type KeepaSearchRunStatus = z.infer<typeof KeepaSearchRunStatus>;

/**
 * `_queued` variants are the same flow as their plain counterparts but
 * record that the user explicitly chose "Queue for later" instead of
 * "Run now" — useful for telemetry and surfacing in the run history.
 * `schedule` is set by Pass 3's cron-driven recurring runs.
 */
export const KeepaSearchRunTrigger = z.enum([
  'manual',
  'manual_queued',
  'template',
  'template_queued',
  'schedule',
]);
export type KeepaSearchRunTrigger = z.infer<typeof KeepaSearchRunTrigger>;

/** Tier inferred from /token at ledger write time. */
export const KeepaTierInferred = z.enum(['free', 'paid', 'unknown']);
export type KeepaTierInferred = z.infer<typeof KeepaTierInferred>;

export const SupplierCandidateStatus = z.enum(['pending', 'promoted', 'dismissed']);
export type SupplierCandidateStatus = z.infer<typeof SupplierCandidateStatus>;

/**
 * Discovery candidate tier — confidence ranking from the design doc.
 *  1 = brand-direct (wholesale@brand.com or brand's own B2B page)
 *  3 = sales-rep agency / trade rep firm publicly listing the brand
 *  4 = trade-press signal ("X distributes Y in North America")
 * Tier 2 (UPC/trademark registrant) was skipped per founder call.
 */
export const SupplierCandidateTier = z.union([z.literal(1), z.literal(3), z.literal(4)]);
export type SupplierCandidateTier = z.infer<typeof SupplierCandidateTier>;

export const KeepaTier = z.enum([
  'basic_19eur',
  'tier_49eur',
  'tier_459eur',
  'tier_1499eur',
  'tier_4499eur',
]);
export type KeepaTier = z.infer<typeof KeepaTier>;

export const Feature = z.enum(['keepa_nl_search', 'outreach_draft', 'pricing_sheet_column_map']);
export type Feature = z.infer<typeof Feature>;

export const Plan = z.enum(['solo', 'operator', 'team']);
export type Plan = z.infer<typeof Plan>;

export const Role = z.enum(['owner', 'operator', 'va', 'warehouse']);
export type Role = z.infer<typeof Role>;

export const ValidationStatus = z.enum(['valid', 'invalid', 'untested']);
export type ValidationStatus = z.infer<typeof ValidationStatus>;

/**
 * SP-API onboarding lifecycle (Phase 1.8 Pass 4).
 *  - not_started        — tenant has never opened the wizard
 *  - awaiting_approval  — wizard opened; Amazon developer-profile review pending
 *  - connected          — credentials verified end-to-end (LWA refresh + 1 SP-API probe)
 *  - failed             — last verify attempt failed; user must fix + retry
 *
 * The Pass 5 SPA wizard reads this from /api/credentials/sp-api/status.
 */
export const SpApiOnboardingStatus = z.enum([
  'not_started',
  'awaiting_approval',
  'connected',
  'failed',
]);
export type SpApiOnboardingStatus = z.infer<typeof SpApiOnboardingStatus>;
